import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Col, Row } from 'reactstrap';
import { getAutomatedReportsData } from '../../../api/reports';
import { InfoLabel, Select } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { durationsAutomatedAccounting } from '../../../constants/reports';
import { fetchDashboardFilterData } from '../../../utils/constant';
import { formatDate, getPrice } from '../../../utils/formatters';
import { apiErrorHandler, getStartDateEndDate } from '../../../utils/handlers';
import NewCommonFilters from '../../dashboard/newCommonFilters';

const initialFilters = {
  cashInFlow: durationsAutomatedAccounting[0],
  revenue: durationsAutomatedAccounting[0],
  balanceDue: durationsAutomatedAccounting[0],
};

const AccountingAutomatedReports = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [accountingAutomatedReportData, setAccountingAutomatedReportData] = useState({
    cashInFlow: {
      totalCashAmount: null,
      lastUpdatedDate: null,
    },
    revenue: { totalAmount: null, lastUpdatedDate: null },
    balanceDue: {
      dueAmount: null,
      lastUpdatedDate: null,
    },
  });
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  const fetchAutomatedReportsData = async (reportType, value) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getStartDateEndDate(value);
      const response = await getAutomatedReportsData(reportType, {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        timezone: moment.tz.guess(),
        ...fetchDashboardFilterData(filterData),
      });
      setAccountingAutomatedReportData((prevState) => {
        return {
          ...prevState,
          [reportType]: response.data.data[0],
        };
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    if (initialRenderDone) {
      // Fetch Updated Values When Selected Common Filter is Changed
      fetchAutomatedReportsData('cashInFlow', filters?.cashInFlow?.value);
      fetchAutomatedReportsData('revenue', filters?.revenue?.value);
      fetchAutomatedReportsData('balanceDue', filters?.balanceDue?.value);
    } else {
      setInitialRenderDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  const handleFilters = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="account-reports">
      <NewCommonFilters />
      <h3 className="account-heading">Accounting Automated Reports</h3>
      <Row>
        <Col lg={4} md={4}>
          <div className="account-label-info">
            <InfoLabel label="Cash In-flow" />
            <Select
              name="cashInFlow"
              formInput={false}
              classNamePrefix="select-dropdown"
              placeholder="Cash In-flow"
              onChange={(val) => {
                handleFilters('cashInFlow', val);
                fetchAutomatedReportsData('cashInFlow', val.value);
              }}
              value={filters.cashInFlow}
              options={durationsAutomatedAccounting}
              size="sm"
            />
          </div>

          <Card className="account-card">
            <InfoLabel label="Total Cash Receipts" />
            <p className="account-p">
              Last updated:
              {formatDate(accountingAutomatedReportData?.cashInFlow?.lastUpdatedDate)}
            </p>
            <h3 className="account-h">
              {getPrice(accountingAutomatedReportData?.cashInFlow?.totalCashAmount)}
            </h3>
          </Card>
        </Col>
        <Col lg={4} md={4}>
          <div className="account-label-info">
            <InfoLabel label="Revenue" />
            <Select
              name="Revenue"
              formInput={false}
              classNamePrefix="select-dropdown"
              placeholder="Revenue"
              onChange={(val) => {
                handleFilters('revenue', val);
                fetchAutomatedReportsData('revenue', val.value);
              }}
              value={filters?.revenue}
              options={durationsAutomatedAccounting}
              size="sm"
            />
          </div>

          <Card className="account-card">
            <InfoLabel label="Total AR Revenue" />
            <p className="account-p">
              Last updated:
              {formatDate(accountingAutomatedReportData?.revenue?.lastUpdatedDate)}
            </p>
            <h3 className="account-h">
              {getPrice(accountingAutomatedReportData?.revenue?.totalAmount)}
            </h3>
          </Card>
        </Col>
        <Col lg={4} md={4}>
          <div className="account-label-info">
            <InfoLabel label=" Pending / Balance Due" />
            <Select
              name="balance due"
              formInput={false}
              classNamePrefix="select-dropdown"
              placeholder="pending/balance due"
              onChange={(val) => {
                handleFilters('balanceDue', val);
                fetchAutomatedReportsData('balanceDue', val.value);
              }}
              value={filters.balanceDue}
              options={durationsAutomatedAccounting}
              size="sm"
            />
          </div>
          <Card className="account-card">
            <InfoLabel label="AR Past Due" />
            <p className="account-p">
              Last updated:
              {formatDate(accountingAutomatedReportData?.balanceDue?.lastUpdatedDate)}
            </p>
            <h3 className="account-h">
              {getPrice(accountingAutomatedReportData?.balanceDue.dueAmount)}
            </h3>
          </Card>
        </Col>
      </Row>
      <hr />
    </div>
  );
};

export default AccountingAutomatedReports;
